// frontend/src/components/PwaAwarenessSnackbar.js

import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Snackbar, Button, Typography, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InstallInstructionsDialog from './InstallInstructionsDialog';
import { useAuth } from '../contexts/AuthContext';
import { matchesRoutePrefix } from '../utils/routeMatch';
import {
  CONSENT_SAVED_EVENT,
  isConsentRequired,
} from './CookieConsent/consentUtils';
import { isStandalonePwa } from '../utils/pwaDisplayMode';
import { useInstallBannerHeld } from '../utils/pwaInstall';
import {
  hasProfession,
  isProfessionInReview,
  shouldShowWelcome,
} from '../utils/onboardingSelectors';

// A FRESH counter (owner decision D21). Under the unversioned key the snackbar
// used to show anywhere - public pages included - so many users spent both
// showings before they ever had an account. It now shows on the dashboard
// only, and everyone gets their two showings there. The old key is left
// behind untouched: nothing reads it any more.
export const STORAGE_KEY = 'pwa-awareness-show-count-v2';
const MAX_SHOWS = 2;
const SHOW_DELAY_MS = 4000;

/** The one route this nudge belongs on. */
const DASHBOARD_ROUTE = '/dashboard';

/**
 * May the install nudge be offered right now? (owner decisions D11 + D21)
 *
 * Only on the dashboard, only to a signed-in user, never while the welcome
 * dialog is still due (it owns the first run and holds the install banners
 * while it is open - GuideContext), and only once the user HAS a profession or
 * one in review: before that there is nothing for an installed app to deliver,
 * and the one ask on the screen is the profession itself.
 *
 * PURE, exported for the test.
 *
 * @param {object} args
 * @param {string} args.pathname
 * @param {boolean} args.isAuthenticated
 * @param {object|null} args.user
 * @returns {boolean}
 */
export const canOfferAwareness = ({ pathname, isAuthenticated, user }) =>
  matchesRoutePrefix(pathname, DASHBOARD_ROUTE) &&
  Boolean(isAuthenticated && user) &&
  !shouldShowWelcome(user) &&
  (hasProfession(user) || isProfessionInReview(user));

const isAndroid = () => /android/i.test(navigator.userAgent);

/**
 * Is the cookie-consent banner still waiting for an answer?
 *
 * The banner is a fixed sheet at the bottom of the screen, where this snackbar
 * also anchors, so a signed-in user who never answered it would see the two
 * stacked over each other. The nudge waits for the answer - the same rule the
 * welcome dialog follows (GuideContext) - and learns about it from
 * CONSENT_SAVED_EVENT rather than polling storage. `isConsentRequired` never
 * throws (a blocked store reads as "still required"), which keeps the nudge
 * down - the same fail-closed direction as its own show-counter.
 *
 * @returns {boolean} true while consent has not been given for this version
 */
const useConsentPending = () => {
  const [pending, setPending] = useState(isConsentRequired);
  useEffect(() => {
    // Re-read on mount too: consent may have been saved between the first
    // render and this listener being attached.
    const sync = () => setPending(isConsentRequired());
    sync();
    window.addEventListener(CONSENT_SAVED_EVENT, sync);
    return () => window.removeEventListener(CONSENT_SAVED_EVENT, sync);
  }, []);
  return pending;
};

/**
 * Hebrew is the app-wide default, so the test is for the explicit opt-out -
 * NOT for `=== 'hebrew'`. `localStorage.language` is written ONLY when a user
 * actively flips the language switch (AuthLayout), so an ABSENT key means
 * "never toggled" and must resolve to Hebrew, matching AuthLayout's
 * `useState(true)` and CookieConsentBanner's `!== 'english'`. Do not "tidy"
 * this back to `=== 'hebrew'`: that shows an English install nudge to every
 * user who never touched the toggle.
 *
 * Wrapped because a blocked/throwing localStorage (Safari private mode) must
 * not take the snackbar down with it; failing to the default is correct here.
 */
const isHebrewPreferred = () => {
  try {
    return localStorage.getItem('language') !== 'english';
  } catch {
    return true;
  }
};

const PwaAwarenessSnackbar = () => {
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const isHebrew = isHebrewPreferred();
  const isRTL = isHebrew;
  // A surface that takes a hold (today: the welcome dialog, the push and the
  // labels announce dialogs - not every dialog) holds the install banners down - the same signal InstallPrompt.js
  // honours (utils/pwaInstall.js). While a hold is out the timer is not armed;
  // when the last hold clears the effect re-runs and re-arms its own delay.
  const bannerHeld = useInstallBannerHeld();
  // Mounted app-globally (App.js, inside <Router> and <AuthProvider>, outside
  // every GuideProvider), so the gate reads both providers directly.
  const { isAuthenticated, user } = useAuth();
  const { pathname } = useLocation();
  const consentPending = useConsentPending();
  const offerable =
    !consentPending && canOfferAwareness({ pathname, isAuthenticated, user });
  // "Already had its turn this page load": the effect re-runs when a hold
  // clears or the gate changes, and the show-counter must still move at most
  // once per load.
  const shownRef = useRef(false);

  // The timer is armed only once EVERY condition holds; leaving the dashboard
  // (or any condition dropping) before it fires disarms it.
  useEffect(() => {
    if (!offerable || bannerHeld || shownRef.current) return undefined;
    if (isStandalonePwa() || isAndroid()) return undefined;

    // A blocked/throwing localStorage (Safari private mode) must not crash the
    // component - this read is inside an effect, so an exception here takes the
    // whole tree down. Fail CLOSED: with no persistable show-count we cannot
    // honour MAX_SHOWS, and nagging on every single load is worse than silence.
    let count;
    try {
      count = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    } catch {
      return undefined;
    }
    if (count >= MAX_SHOWS) return undefined;

    const timer = setTimeout(() => {
      shownRef.current = true;
      setOpen(true);
      try {
        localStorage.setItem(STORAGE_KEY, String(count + 1));
      } catch {
        /* storage unavailable - the show-count simply does not persist */
      }
    }, SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, [bannerHeld, offerable]);

  // Shown, then the user leaves the dashboard (or signs out): it goes for the
  // rest of this load rather than following them onto another screen.
  useEffect(() => {
    if (!offerable) setOpen(false);
  }, [offerable]);

  const handleClose = () => setOpen(false);

  const handleHowClick = () => {
    setOpen(false);
    setDialogOpen(true);
  };

  const snackbarMessage = isHebrew
    ? 'ניתן להתקין את Leadify AI כאפליקציה בטלפון!'
    : 'You can install Leadify AI as an app on your phone!';

  return (
    <>
      <Snackbar
        // Also hidden while a hold is out. Unlike InstallPrompt's banner, whose
        // z-index sits below every modal, a MUI Snackbar (z 1400) would render
        // OVER an open dialog (z 1300). It comes back when the hold clears.
        open={open && !bannerHeld}
        onClose={handleClose}
        // Removed from every prerendered snapshot by scripts/prerender.js
        // (backlog #158). It opens on a 4s timer and used to run on every
        // page, so the /login and /register snapshots (which may wait up to
        // 8s for Google's button) could bake it in. It is dashboard-only and
        // signed-in-only now, so no prerendered route should ever reach it;
        // the marker stays as the belt. No presence assertion: it does NOT
        // open during a prerender.
        data-prerender-strip="pwa-snackbar"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            background: 'linear-gradient(135deg, #ccfbf1 0%, #e0f7f4 60%, #fce7f3 100%)',
            color: '#0f766e',
            borderRadius: '12px',
            direction: isRTL ? 'rtl' : 'ltr',
            boxShadow: '0 4px 20px rgba(13, 148, 136, 0.2)',
            border: '1px solid rgba(13, 148, 136, 0.2)',
            minWidth: { xs: 'auto', sm: '400px' },
            maxWidth: 'calc(100vw - 32px)',
          },
          '& .MuiSnackbarContent-message': {
            width: '100%',
            padding: '8px 4px',
          },
        }}
        message={
          <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <IconButton
              size="small"
              onClick={handleClose}
              aria-label={isHebrew ? 'סגור' : 'close'}
              sx={{
                position: 'absolute',
                top: 0,
                [isRTL ? 'left' : 'right']: 0,
                color: '#0f766e',
                p: 0.5,
                '&:hover': { background: 'rgba(13, 148, 136, 0.08)' },
              }}
            >
              <CloseIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
            <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', textAlign: 'center', color: '#0f766e', px: 3 }}>
              {snackbarMessage}
            </Typography>
            <Button
              size="small"
              onClick={handleHowClick}
              sx={{
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '0.85rem',
                background: '#f3f4f6',
                color: '#374151',
                border: '1.5px solid #d1d5db',
                borderRadius: '8px',
                px: 1.5,
                '&:hover': { background: '#e5e7eb' },
              }}
            >
              {isHebrew ? 'לחץ לפרטים' : 'How to install?'}
            </Button>
          </Box>
        }
      />

      <InstallInstructionsDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
};

export default PwaAwarenessSnackbar;
